
import { computed, defineComponent, onUnmounted, ref, watch } from 'vue';

interface Mark {
  value: number;
  label: string;
}

const __default__ = defineComponent({
  name: 'AppSlider',

  props: {
    // Основные параметры
    modelValue: {
      type: Number,
      required: true,
    },
    min: {
      type: Number,
      default: 0,
    },
    max: {
      type: Number,
      default: 100,
    },
    step: {
      type: Number,
      default: 1,
    },

    // Опции отображения
    showTooltip: {
      type: Boolean,
      default: true,
    },
    showMarks: {
      type: Boolean,
      default: false,
    },
    showInput: {
      type: Boolean,
      default: false,
    },
    marks: {
      type: Array as () => Mark[],
      default: () => [],
    },

    // Форматирование
    suffix: {
      type: String,
      default: '',
    },
    format: {
      type: Function as unknown as () => ((value: number) => string) | null,
      default: null,
    },

    // Стили
    vertical: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },

    // Цвета
    trackColor: {
      type: String,
      default: '#e0e0e0',
    },
    progressColor: {
      type: String,
      default: '#4CAF50',
    },
    thumbColor: {
      type: String,
      default: '#4CAF50',
    },
  },

  emits: ['update:modelValue', 'change', 'input', 'drag-start', 'drag-end'],

  setup(props, { emit }) {
    // Refs
    const sliderThumb = ref<HTMLElement | null>(null);
    const isDragging = ref(false);
    const inputValue = ref(props.modelValue);

    // Computed
    const normalizedValue = computed(() => {
      let value = props.modelValue;

      // Ограничиваем значение в пределах min/max
      if (value < props.min) value = props.min;
      if (value > props.max) value = props.max;

      // Округляем до ближайшего step
      const steps = Math.round((value - props.min) / props.step);
      return props.min + steps * props.step;
    });

    const focusThumb = (event: MouseEvent) => {
      event.stopPropagation();
      if (sliderThumb.value) {
        sliderThumb.value.focus();
      }
    };

    // Форматирование значения
    const formatValue = (value: number): string => {
      if (props.format && typeof props.format === 'function') {
        return props.format(value);
      }
      return value + props.suffix;
    };

    // Обработка начала перетаскивания
    const startDrag = (event: MouseEvent | TouchEvent): void => {
      if (props.disabled) return;

      isDragging.value = true;
      emit('drag-start', normalizedValue.value);

      // Предотвращаем выделение текста
      event.preventDefault();

      // Добавляем обработчики для всего документа
      document.addEventListener('mousemove', handleDrag);
      document.addEventListener('touchmove', handleDrag, { passive: false });
      document.addEventListener('mouseup', stopDrag);
      document.addEventListener('touchend', stopDrag);
      document.addEventListener('touchcancel', stopDrag);
    };

    // Обработка перетаскивания
    const handleDrag = (event: MouseEvent | TouchEvent): void => {
      if (!isDragging.value || props.disabled) return;

      event.preventDefault();

      const clientX = (event as MouseEvent).clientX || (event as TouchEvent).touches?.[0]?.clientX;
      if (!clientX) return;

      // Получаем позицию относительно слайдера
      const sliderRect = sliderThumb.value?.parentElement?.getBoundingClientRect();
      if (!sliderRect) return;

      const offsetX = clientX - sliderRect.left;
      const width = sliderRect.width;

      // Рассчитываем новое значение
      let percentage = offsetX / width;
      if (percentage < 0) percentage = 0;
      if (percentage > 1) percentage = 1;

      let newValue = props.min + percentage * (props.max - props.min);

      // Округляем до step
      const steps = Math.round((newValue - props.min) / props.step);
      newValue = props.min + steps * props.step;

      // Ограничиваем min/max
      newValue = Math.max(props.min, Math.min(props.max, newValue));

      // Обновляем значение
      updateValue(newValue);
    };

    // Остановка перетаскивания
    const stopDrag = (): void => {
      if (!isDragging.value) return;

      isDragging.value = false;
      emit('drag-end', normalizedValue.value);

      // Удаляем обработчики
      document.removeEventListener('mousemove', handleDrag);
      document.removeEventListener('touchmove', handleDrag);
      document.removeEventListener('mouseup', stopDrag);
      document.removeEventListener('touchend', stopDrag);
      document.removeEventListener('touchcancel', stopDrag);
    };

    // Обработка клика по слайдеру
    const handleSliderClick = (event: MouseEvent): void => {
      if (props.disabled) return;

      const sliderRect = sliderThumb.value?.parentElement?.getBoundingClientRect();
      if (!sliderRect) return;

      const offsetX = event.clientX - sliderRect.left;
      const width = sliderRect.width;

      let percentage = offsetX / width;
      if (percentage < 0) percentage = 0;
      if (percentage > 1) percentage = 1;

      let newValue = props.min + percentage * (props.max - props.min);

      // Округляем до step
      const steps = Math.round((newValue - props.min) / props.step);
      newValue = props.min + steps * props.step;

      updateValue(newValue);
    };

    // Обработка клика по метке
    const handleMarkClick = (value: number): void => {
      if (props.disabled) return;
      updateValue(value);
    };

    // Обработка клавиатуры
    const handleKeydown = (event: KeyboardEvent): void => {
      if (props.disabled) return;

      let newValue = normalizedValue.value;

      switch (event.key) {
        case 'ArrowLeft':
        case 'ArrowDown':
          newValue -= props.step;
          event.preventDefault();
          break;
        case 'ArrowRight':
        case 'ArrowUp':
          newValue += props.step;
          event.preventDefault();
          break;
        case 'Home':
          newValue = props.min;
          event.preventDefault();
          break;
        case 'End':
          newValue = props.max;
          event.preventDefault();
          break;
        case 'PageDown':
          newValue -= props.step * 10;
          event.preventDefault();
          break;
        case 'PageUp':
          newValue += props.step * 10;
          event.preventDefault();
          break;
        default:
          return;
      }

      // Ограничиваем значение
      newValue = Math.max(props.min, Math.min(props.max, newValue));

      updateValue(newValue);
    };

    // Обработка изменения через input
    const handleInputChange = (): void => {
      let value = parseFloat(String(inputValue.value));

      if (isNaN(value)) {
        inputValue.value = normalizedValue.value;
        return;
      }

      // Ограничиваем значение
      value = Math.max(props.min, Math.min(props.max, value));

      // Округляем до step
      const steps = Math.round((value - props.min) / props.step);
      value = props.min + steps * props.step;

      updateValue(value);
      inputValue.value = value;
    };

    // Обновление значения
    const updateValue = (newValue: number): void => {
      const oldValue = normalizedValue.value;

      if (newValue !== oldValue && !props.disabled) {
        emit('update:modelValue', newValue);
        emit('change', newValue, oldValue);
        emit('input', newValue);
        inputValue.value = newValue;
      }
    };

    // Синхронизация inputValue с modelValue
    watch(
      () => props.modelValue,
      (newValue: number) => {
        inputValue.value = newValue;
      },
      { immediate: true }
    );

    // Убираем слушатели при размонтировании
    onUnmounted(() => {
      stopDrag();
    });

    // Возвращаем все методы и значения для использования в шаблоне
    return {
      // refs
      sliderThumb,
      inputValue,

      // computed
      normalizedValue,

      // methods
      formatValue,
      startDrag,
      handleSliderClick,
      handleMarkClick,
      handleKeydown,
      handleInputChange,

      // props для использования в шаблоне
      props,
      focusThumb,
    };
  },
});

import { useCssVars as _useCssVars } from 'vue'
const __injectCSSVars__ = () => {
_useCssVars(_ctx => ({
  "7602f659": (_ctx.props.trackColor),
  "153b4415": (_ctx.props.progressColor),
  "6ade56a4": (_ctx.props.thumbColor),
  "d1c655a2": (`${((_ctx.normalizedValue - _ctx.props.min) / (_ctx.props.max - _ctx.props.min)) * 100}%`),
  "e35e585e": (`${100 - ((_ctx.normalizedValue - _ctx.props.min) / (_ctx.props.max - _ctx.props.min)) * 100}%`)
}))}
const __setup__ = __default__.setup
__default__.setup = __setup__
  ? (props, ctx) => { __injectCSSVars__();return __setup__(props, ctx) }
  : __injectCSSVars__

export default __default__